// Scored Fatigue dashboard: page chrome (title, screen nav, drill-scope switcher,
// breadcrumb) plus the active screen. Ports the prototype's sidebar/topbar to a
// horizontal nav so it sits inside the existing admin layout.

import React from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Activity,
  Building2,
  ChevronRight,
  CircleCheck,
  Clock3,
  Gauge,
  LayoutGrid,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Truck,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { qk } from '../../../../../lib/queryKeys';
import { useClientScope } from '../../../../../shared/contexts/ClientScopeContext';
import { fatigueApi } from '../data/fatigueApi';
import { DEMO_MODE, GROUPS, REGIONS, WORKERS } from './engine/data';
import { loadLiveRoster } from './engine/live';
import { RotaContext } from './RotaContext';
import { LiveRotaScreen } from './screens/LiveRotaScreen';
import { Banner } from './ui';
import { DashProvider, useDash } from './state';
import type { DrillScope, ScreenKey } from './state';
import { OverviewScreen } from './screens/OverviewScreen';
import { PastScreen } from './screens/PastScreen';
import { TodayScreen } from './screens/TodayScreen';
import { ForwardScreen } from './screens/ForwardScreen';
import { ShiftsScreen } from './screens/ShiftsScreen';
import { DrivingScreen } from './screens/DrivingScreen';
import { ResilienceScreen } from './screens/ResilienceScreen';
import { ComplianceScreen } from './screens/ComplianceScreen';
import { RecommendationsScreen } from './screens/RecommendationsScreen';
import { WorkerScreen } from './screens/WorkerScreen';
import '../../../../../styles/fatigue-shadcn.css';

const NAV: { key: ScreenKey; label: string; icon: React.ElementType }[] = [
  { key: 'overview', label: 'Overview', icon: LayoutGrid },
  { key: 'past', label: 'Past', icon: Clock3 },
  { key: 'today', label: 'Today', icon: Gauge },
  { key: 'forward', label: 'Forward', icon: TrendingUp },
  { key: 'shifts', label: 'Shifts & Sleep', icon: Activity },
  { key: 'driving', label: 'Driving', icon: Truck },
  { key: 'resilience', label: 'OT Resilience', icon: Activity },
  { key: 'compliance', label: 'HSE Compliance', icon: ShieldCheck },
  { key: 'recommendations', label: 'AI Recommendations', icon: Sparkles },
];

const SCOPES: { key: DrillScope; label: string }[] = [
  { key: 'company', label: 'Company' },
  { key: 'region', label: 'Region' },
  { key: 'group', label: 'Group' },
  { key: 'individual', label: 'Individual' },
];

const rotaGated = (kind: 'shifts' | 'driving' | 'resilience' | 'compliance', Screen: React.FC): React.FC =>
  function Gated() {
    return DEMO_MODE.live ? <LiveRotaScreen kind={kind} /> : <Screen />;
  };

const SCREENS: Record<ScreenKey, React.FC> = {
  overview: OverviewScreen,
  past: PastScreen,
  today: TodayScreen,
  forward: ForwardScreen,
  shifts: rotaGated('shifts', ShiftsScreen),
  driving: rotaGated('driving', DrivingScreen),
  resilience: rotaGated('resilience', ResilienceScreen),
  compliance: rotaGated('compliance', ComplianceScreen),
  recommendations: RecommendationsScreen,
  worker: WorkerScreen,
};

const Breadcrumb: React.FC = () => {
  const d = useDash();
  const crumbs: { label: string; onClick?: () => void }[] = [{ label: 'Company', onClick: d.resetToCompany }];
  if (d.activeRegion) {
    crumbs.push({ label: REGIONS[d.activeRegion].name, onClick: () => d.setDrillScope('region') });
  }
  if (d.activeGroup !== null) {
    crumbs.push({ label: GROUPS[d.activeGroup].name, onClick: () => d.setDrillScope('group') });
  }
  if (d.activeWorker && (d.drillScope === 'individual' || d.screen === 'worker')) {
    const name = WORKERS[d.activeWorker].name;
    crumbs.push({ label: name, onClick: d.screen === 'worker' ? undefined : () => d.go('worker') });
  }
  return (
    <nav className='flex flex-wrap items-center gap-1.5 text-[13px] text-muted-foreground' aria-label='Scope'>
      {crumbs.map((c, i) => (
        <React.Fragment key={`${c.label}-${i}`}>
          {i > 0 && <ChevronRight className='h-3.5 w-3.5' />}
          {c.onClick ? (
            <button type='button' onClick={c.onClick} className='text-brand hover:underline'>
              {c.label}
            </button>
          ) : (
            <span className='text-foreground'>{c.label}</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
};

const DashboardBody: React.FC<{ asOf: string | null }> = ({ asOf }) => {
  const d = useDash();
  const Screen = SCREENS[d.screen];
  const all = Object.values(WORKERS);
  const total = all.length;
  const warming = all.filter((w) => w.coverage?.baselineWarming).length;
  return (
    <div className='mx-auto flex w-full max-w-[1600px] flex-col gap-5'>
      <div className='flex flex-wrap items-center justify-between gap-5'>
        <div className='flex min-w-0 items-center gap-3.5'>
          <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-brand/15 bg-brand/10 text-brand'>
            <Activity className='h-6 w-6' aria-hidden='true' />
          </div>
          <div className='min-w-0'>
            <h1 className='text-2xl font-semibold tracking-tight sm:text-[28px]'>Fatigue Management</h1>
            <p className='mt-1 text-sm text-muted-foreground'>
              Fatigue exposure, circadian disruption and load readiness.
            </p>
          </div>
        </div>
        <div
          className='fm-surface flex max-w-full gap-1 overflow-x-auto rounded-xl border border-border bg-card p-1'
          role='group'
          aria-label='Drill scope'
        >
          {SCOPES.map((s) => (
            <button
              key={s.key}
              type='button'
              aria-pressed={d.drillScope === s.key}
              onClick={() => d.setDrillScope(s.key)}
              className={cn(
                'min-h-9 rounded-lg px-3.5 py-2 text-xs font-medium transition-colors',
                d.drillScope === s.key
                  ? 'bg-brand/10 text-brand'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <nav
        className='fm-surface overflow-x-auto rounded-xl border border-border bg-card p-1.5'
        aria-label='Fatigue Management views'
      >
        <div className='flex min-w-max gap-1'>
          {NAV.map(({ key, label, icon: Icon }) => {
            const active = d.screen === key || (d.screen === 'worker' && key === 'overview');
            return (
              <button
                key={key}
                type='button'
                onClick={() => d.go(key)}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'flex min-h-10 items-center gap-2 rounded-lg px-3.5 py-2.5 text-[13px] font-medium transition-colors',
                  active ? 'bg-brand/10 text-brand' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                <Icon className='h-4 w-4' aria-hidden='true' />
                {label}
              </button>
            );
          })}
        </div>
      </nav>

      <div className='flex flex-wrap items-center justify-between gap-x-5 gap-y-2'>
        <div className='flex items-center gap-2 text-muted-foreground'>
          <Building2 className='h-4 w-4' aria-hidden='true' />
          <Breadcrumb />
        </div>
        {asOf ? (
          <div className='flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground'>
            <span className='inline-flex items-center gap-1.5 font-medium text-success'>
              <span className='h-1.5 w-1.5 rounded-full bg-success' aria-hidden='true' />
              Live wearable data
            </span>
            <span>Latest reading {asOf}</span>
            <span>
              {all.some((w) => w.coverage?.rota) ? 'Shift schedules connected' : 'Load scores need rota data'}
            </span>
            {warming > 0 && (
              <span>
                {warming} of {total} on a short baseline (&lt;14 days)
              </span>
            )}
          </div>
        ) : (
          <span className='inline-flex items-center gap-1 rounded-[4px] bg-warning/10 px-1.5 py-0.5 text-[11px] font-medium text-warning'>
            <CircleCheck className='h-3 w-3' /> Demo data
          </span>
        )}
      </div>

      {all.some((w) => w.rota?.some((s) => s.source === 'demo')) && (
        <Banner tone='warn'>
          <span>
            <strong>Demo rota:</strong> Shift schedules are illustrative. Wearable readings remain live.
          </span>
        </Banner>
      )}
      <Screen />
    </div>
  );
};

const Message: React.FC<{ title: string; children?: React.ReactNode }> = ({ title, children }) => (
  <div className='rounded-md border border-border px-6 py-14 text-center'>
    <div className='text-sm font-semibold'>{title}</div>
    {children && <p className='mx-auto mt-1.5 max-w-md text-sm text-muted-foreground'>{children}</p>}
  </div>
);

const LiveDashboard: React.FC<{ clientId: string }> = ({ clientId }) => {
  const q = useQuery({
    queryKey: qk.fatigue.workforce(clientId),
    queryFn: () => fatigueApi.getWorkforce(clientId),
    staleTime: 5 * 60_000,
  });
  // Swap the engine roster once per payload, before any screen reads it.
  const asOf = React.useMemo(() => {
    if (!q.data?.workers.length) return null;
    loadLiveRoster(q.data);
    return q.data.asOf;
  }, [q.data]);

  if (q.isLoading) return <Message title='Loading fatigue data…' />;
  if (q.isError) {
    const status = (q.error as { response?: { status?: number; data?: { message?: string } } })?.response;
    return (
      <Message title='Fatigue data is not available'>
        {status?.status === 403 ? status.data?.message : 'The request failed. Try again shortly.'}
      </Message>
    );
  }
  if (!q.data?.workers.length) {
    return (
      <Message title='No scorable wearable data'>
        None of the {q.data?.enrolled ?? 0} people in this client have enough HRV, resting-HR and sleep history yet.
      </Message>
    );
  }
  return (
    <RotaContext.Provider
      value={{ clientId, workers: q.data.workers.map((w) => ({ id: w.id, name: w.name })), shifts: q.data.rota ?? [] }}
    >
      <DashProvider key={`${clientId}-${asOf}`}>
        <DashboardBody asOf={asOf} />
      </DashProvider>
    </RotaContext.Provider>
  );
};

export const FatigueDashboard: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { effectiveClientId } = useClientScope();
  return (
    <div className={cn('fatigue-page min-h-full p-4 sm:p-6 xl:p-8', isDark && 'dark')}>
      {effectiveClientId ? (
        <LiveDashboard clientId={effectiveClientId} />
      ) : (
        <Message title='Select a client'>
          The scored fatigue dashboard reads one client at a time. Choose a client in the client selector.
        </Message>
      )}
    </div>
  );
};
